import { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, EyeOff, Eye, ArrowLeft, ChevronRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import PageTransition from "@/components/PageTransition";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CustomInput } from "@/components/ui/custom-input";
import { Separator } from "@/components/ui/separator";

const Auth = () => {
  const [isSignIn, setIsSignIn] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const { signIn, signUp, signInWithGoogle, loading, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Check if user just signed up
  const justSignedUp = location.state?.justSignedUp;
  const wantsToSignUp = location.state?.isSignUp;

  useEffect(() => {
    // If user is already logged in, redirect to dashboard
    if (user) {
      navigate("/dashboard");
    }
    
    // Set mode based on location state
    if (justSignedUp) {
      setIsSignIn(true);
      toast.info("Please verify your email before signing in", {
        description: "Check your inbox for a verification link",
        position: "bottom-right",
        duration: 6000,
        icon: <Mail className="h-5 w-5" />
      });
    }

    if (wantsToSignUp) {
      setIsSignIn(false);
    }
  }, [user, navigate, justSignedUp, wantsToSignUp]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }
    
    if (!isSignIn && password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (isSignIn) {
      await signIn(email, password);
    } else {
      await signUp(email, password);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle();
    } catch (error: any) {
      toast.error(error.message || "Error signing in with Google");
      console.error("Google sign-in error:", error);
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        duration: 0.6,
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0,
      opacity: 1,
      transition: { duration: 0.4 }
    }
  };

  return (
    <PageTransition className="min-h-screen flex flex-col items-center justify-center bg-white px-4 py-16">
      <div className="absolute inset-0 overflow-hidden -z-10">
        <div className="absolute inset-0 bg-grid-pattern bg-[size:30px_30px] opacity-5"></div>
        <div className="absolute top-0 left-0 w-full h-64 bg-gradient-to-b from-expitrack-primary/5 to-transparent"></div>
        <div className="absolute bottom-0 right-0 w-full h-64 bg-gradient-to-t from-expitrack-primary/5 to-transparent"></div>
        <div className="absolute top-1/4 -left-10 w-48 h-48 bg-expitrack-primary/20 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-1/4 -right-10 w-48 h-48 bg-green-400/20 rounded-full filter blur-3xl"></div>
      </div>

      <motion.div 
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="w-full max-w-md"
      >
        <div className="flex justify-center mb-6">
          <Link to="/" className="flex items-center text-gray-900 font-bold text-2xl">
            <span className="text-expitrack-primary">Expi</span>Track
          </Link>
        </div>
        
        <motion.div 
          variants={itemVariants}
          className="bg-white shadow-xl rounded-xl p-8 md:p-10 overflow-hidden relative border border-gray-100"
        >
          <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-expitrack-primary via-green-400 to-expitrack-primary"></div>
  
          <Link to="/" className="inline-flex items-center text-gray-600 hover:text-expitrack-primary mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to home
          </Link>
          
          <motion.div variants={itemVariants}>
            <h1 className="text-2xl md:text-3xl font-bold mb-2 text-gray-900">
              {isSignIn ? "Welcome back" : "Create an account"}
            </h1>
            <p className="text-gray-600 mb-8">
              {isSignIn 
                ? "Sign in to access your ExpiTrack dashboard" 
                : "Join ExpiTrack to start managing expirations"}
            </p>
          </motion.div>

          <motion.div variants={itemVariants} className="mb-6 space-y-4">
            <button
              onClick={handleGoogleSignIn}
              className="w-full flex items-center justify-center gap-3 bg-white p-3 rounded-xl border border-gray-300 hover:bg-gray-50 transition-colors shadow-sm"
              disabled={loading}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21.8055 10.0415H21V10H12V14H17.6515C16.827 16.3285 14.6115 18 12 18C8.6865 18 6 15.3135 6 12C6 8.6865 8.6865 6 12 6C13.5295 6 14.921 6.577 15.9805 7.5195L18.809 4.691C17.023 3.0265 14.634 2 12 2C6.4775 2 2 6.4775 2 12C2 17.5225 6.4775 22 12 22C17.5225 22 22 17.5225 22 12C22 11.3295 21.931 10.675 21.8055 10.0415Z" fill="#FFC107"/>
                <path d="M3.15302 7.3455L6.43852 9.755C7.32752 7.554 9.48052 6 12 6C13.5295 6 14.921 6.577 15.9805 7.5195L18.809 4.691C17.023 3.0265 14.634 2 12 2C8.15902 2 4.82802 4.1685 3.15302 7.3455Z" fill="#FF3D00"/>
                <path d="M12 22C14.583 22 16.93 21.0115 18.7045 19.404L15.6095 16.785C14.5718 17.5742 13.3038 18.001 12 18C9.39897 18 7.19047 16.3415 6.35847 14.027L3.09747 16.5395C4.75247 19.778 8.11347 22 12 22Z" fill="#4CAF50"/>
                <path d="M21.8055 10.0415H21V10H12V14H17.6515C17.2571 15.1082 16.5467 16.0766 15.608 16.7855L15.6095 16.7845L18.7045 19.4035C18.4855 19.6025 22 17 22 12C22 11.3295 21.931 10.675 21.8055 10.0415Z" fill="#1976D2"/>
              </svg>
              <span className="font-medium text-gray-700">
                {isSignIn ? "Sign in with Google" : "Sign up with Google"}
              </span>
            </button>
          </motion.div>

          <motion.div variants={itemVariants} className="relative flex items-center my-6">
            <div className="flex-grow border-t border-gray-300"></div>
            <span className="flex-shrink mx-4 text-gray-500 text-sm">or continue with email</span>
            <div className="flex-grow border-t border-gray-300"></div>
          </motion.div>

          <motion.form variants={itemVariants} onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="relative">
                <CustomInput
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@example.com"
                  className="pl-10 py-3 bg-white border-gray-200 focus:border-expitrack-primary focus:ring-expitrack-primary/20"
                  disabled={loading}
                />
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="relative">
                <CustomInput
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 pr-10 py-3 bg-white border-gray-200 focus:border-expitrack-primary focus:ring-expitrack-primary/20"
                  disabled={loading}
                />
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {!isSignIn && (
              <div className="space-y-2">
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  Confirm Password
                </label>
                <div className="relative">
                  <CustomInput
                    id="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    className="pl-10 py-3 bg-white border-gray-200 focus:border-expitrack-primary focus:ring-expitrack-primary/20"
                    disabled={loading}
                  />
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                </div>
              </div>
            )}

            <Button
              type="submit"
              className="w-full py-6 bg-expitrack-primary text-white hover:bg-expitrack-primary/90 transition-colors shadow-lg shadow-expitrack-primary/20 flex items-center justify-center"
              disabled={loading}
            >
              {loading
                ? "Processing..."
                : isSignIn
                  ? "Sign In"
                  : "Create Account"}
              <ChevronRight className="ml-2 w-5 h-5" />
            </Button>
            
            {isSignIn && (
              <div className="text-center">
                <button
                  type="button"
                  className="text-sm text-expitrack-primary hover:underline font-medium"
                >
                  Forgot your password?
                </button>
              </div>
            )}
          </motion.form>

          <motion.div variants={itemVariants} className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              {isSignIn ? "Don't have an account? " : "Already have an account? "}
              <button
                type="button"
                onClick={() => setIsSignIn(!isSignIn)}
                className="text-expitrack-primary font-medium hover:underline transition-colors"
              >
                {isSignIn ? "Sign up" : "Sign in"}
              </button>
            </p>
          </motion.div>
        </motion.div>
        
        <motion.div variants={itemVariants} className="text-center mt-4 text-xs text-gray-500">
          By signing in, you agree to our 
          <a href="#" className="text-expitrack-primary hover:underline mx-1">Terms of Service</a>
          and
          <a href="#" className="text-expitrack-primary hover:underline ml-1">Privacy Policy</a>
        </motion.div>
      </motion.div>
    </PageTransition>
  );
};

export default Auth;
